<template>
  <!--
    Sfondo delle schermate pubbliche (login, 404): un mesh di macchie sfocate
    che derivano lentamente l'una sull'altra. È decorazione, quindi sta fuori
    dall'albero di accessibilità e non intercetta il puntatore. Vive solo qui:
 dentro l'app la superficie resta piatta.

    Le macchie restano nella famiglia cromatica del sistema — verde smeraldo
    modulato in luminosità e con escursioni di tinta contenute (150-195) — così
    il risultato è ricco senza introdurre colori estranei al brand.
  -->
  <div aria-hidden="true" class="pointer-events-none absolute inset-0 overflow-hidden">
    <div class="mesh absolute">
      <span class="macchia macchia--smeraldo"></span>
      <span class="macchia macchia--profonda"></span>
      <span class="macchia macchia--menta"></span>
      <span class="macchia macchia--acqua"></span>
      <span class="macchia macchia--chiara"></span>
    </div>
    <div class="grana absolute inset-0"></div>
  </div>
</template>

<style scoped>
.mesh {
  /* Il gruppo deborda dalla finestra: le macchie sfumano verso il trasparente
     ai propri bordi, e senza questo margine la sfumatura si leggerebbe come
     una fascia più chiara lungo i lati dello schermo. */
  inset: -12%;
}

/**
 * Il blur sta sulla singola macchia, non sul gruppo: così ogni macchia è un
 * layer composito che il browser sfoca una volta e poi si limita a spostare.
 * Con il filtro sul contenitore, ogni fotogramma avrebbe richiesto di rifare
 * il blur dell'intera superficie.
 *
 * Ogni macchia parte dal proprio posto e si muove su un percorso diverso, con
 * durate prime fra loro: gli incroci non si ripetono mai uguali.
 */
.macchia {
  position: absolute;
  border-radius: 50%;
  filter: blur(56px);
  will-change: transform;
}

.macchia--smeraldo {
  top: -22%;
  left: 6%;
  width: 46vmax;
  height: 46vmax;
  background: radial-gradient(
    circle,
    color-mix(in oklch, var(--color-accent) 62%, transparent),
    transparent 68%
  );
  animation: derivaA 16s ease-in-out infinite alternate;
}

.macchia--profonda {
  top: 22%;
  right: -6%;
  width: 48vmax;
  height: 48vmax;
  background: radial-gradient(circle, oklch(0.6 0.13 176 / 0.55), transparent 66%);
  animation: derivaB 21s ease-in-out infinite alternate;
}

.macchia--menta {
  bottom: -20%;
  left: -8%;
  width: 50vmax;
  height: 50vmax;
  background: radial-gradient(circle, oklch(0.86 0.1 158 / 0.7), transparent 68%);
  animation: derivaC 19s ease-in-out infinite alternate;
}

.macchia--acqua {
  right: 16%;
  bottom: -24%;
  width: 38vmax;
  height: 38vmax;
  background: radial-gradient(circle, oklch(0.74 0.11 199 / 0.62), transparent 66%);
  animation: derivaD 25s ease-in-out infinite alternate;
}

/* La macchia chiara tiene libera la zona centrale, dove sta il contenuto:
   è quella che garantisce il contrasto del testo sopra il mesh. */
.macchia--chiara {
  top: 6%;
  left: 24%;
  width: 48vmax;
  height: 38vmax;
  background: radial-gradient(circle, oklch(0.985 0.004 165 / 0.88), transparent 62%);
  animation: derivaE 20s ease-in-out infinite alternate;
}

@keyframes derivaA {
  from {
    transform: translate3d(-6%, -3%, 0) scale(1);
  }
  to {
    transform: translate3d(8%, 6%, 0) scale(1.18);
  }
}

@keyframes derivaB {
  from {
    transform: translate3d(4%, 5%, 0) scale(1.12);
  }
  to {
    transform: translate3d(-9%, -6%, 0) scale(1);
  }
}

@keyframes derivaC {
  from {
    transform: translate3d(-4%, 4%, 0) scale(1.06);
  }
  to {
    transform: translate3d(10%, -5%, 0) scale(1.2);
  }
}

@keyframes derivaD {
  from {
    transform: translate3d(6%, 2%, 0) scale(1);
  }
  to {
    transform: translate3d(-7%, -7%, 0) scale(1.15);
  }
}

@keyframes derivaE {
  from {
    transform: translate3d(3%, -2%, 0) scale(1.08);
  }
  to {
    transform: translate3d(-5%, 4%, 0) scale(1);
  }
}

/**
 * Grana finissima sopra il mesh: rompe il banding delle sfumature ampie sui
 * monitor a 8 bit e dà alla superficie una consistenza da carta invece che da
 * campitura digitale.
 */
.grana {
  opacity: 0.22;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/><feColorMatrix type='saturate' values='0'/></filter><rect width='120' height='120' filter='url(%23g)' opacity='0.5'/></svg>");
  background-size: 120px 120px;
}

/**
 * Tema scuro: le macchie più chiare, tarate per un fondo luminoso, su scuro
 * diventerebbero patch abbaglianti. La `--chiara` — che in chiaro schiarisce il
 * centro per il contrasto del testo — su scuro fa il lavoro OPPOSTO: si tinge
 * come la superficie scura per calmare il centro dove sta il contenuto. Le
 * altre restano nella famiglia verde, solo più profonde. (Selettori scoped:
 * Vue applica lo scope alla sola classe finale, quindi l'antenato
 * `[data-theme='dark']` funziona.)
 */
:root[data-theme='dark'] .macchia--menta {
  background: radial-gradient(circle, oklch(0.6 0.11 158 / 0.45), transparent 68%);
}

:root[data-theme='dark'] .macchia--acqua {
  background: radial-gradient(circle, oklch(0.58 0.1 199 / 0.45), transparent 66%);
}

:root[data-theme='dark'] .macchia--chiara {
  background: radial-gradient(circle, oklch(0.24 0.012 170 / 0.85), transparent 62%);
}

/* Chi ha disattivato il movimento vede la stessa composizione, ferma. */
@media (prefers-reduced-motion: reduce) {
  .macchia {
    animation: none;
  }
}
</style>
